import {PrinterOutlined} from '@ant-design/icons';
import {Button, Radio} from 'antd';
import cn from 'classnames';
import {omit} from 'lodash-es';
import React, {useMemo} from 'react';
import {useHistory} from 'react-router-dom';

import {
    GroupingSettingsModalOpener,
} from 'components/report-configuration/tabs/report-table-settings/modals/grouping-settings-modal-opener';
import {REQUESTS_ENTITY_NAME} from 'components/request/constants/requests.constants';
import {
    AttachmentButton,
} from 'components/table-report/components/table-report-actions/actions/attachment-button/attachment-button';
import {
    getGroupingAttributes,
} from 'components/table-report/utils/table-report-utils';
import {TableOpenModalAction} from 'components/table/action/open-modal';
import {EMetaActionType} from 'entities/metadata';
import {ControlRatiosActionsButtons} from 'features/calculate-control-ratios';
import {ActionsKebabMenu} from 'features/configure-table-report-actions-menu';
import {ReactComponent as ArrowBackOutlined} from 'shared/assets/arrow-back-outlined.svg';
import {TStateSetter} from 'shared/types/generics';
import {useAppSelector} from 'store/config/hooks';
import {
    EReportConfigurationPosition,
} from 'store/slices/report-configuration-slice/report-configuration-dto';
import {tableReportSliceActions} from 'store/slices/table-report-slice';
import {selectTableReportColumnFiltersDataAdaptedForRequest} from 'store/slices/table-report-slice/table-report-column-filters/table-report-column-filters-selectors';
import {selectDocIdsToRequest} from 'store/slices/table-report-slice/table-report-row-attachments/table-report-row-attachments-selectors';
import {
    selectLastTableReportLoadPageRequestArgs,
    selectTableReportComparisonOptions,
    selectTableReportTemplateConfig,
} from 'store/slices/table-report-slice/table-report-slice-selectors';
import {IReportPagination, TTableReportBodyItem, TTableReportColumn} from 'store/slices/table-report-slice/table-report-slice-types';
import {EUserRoleFunction} from 'store/slices/user-slice/user-slice-role-functions';

import {EReportViewMode, EDesignTemplates} from '../../table-report-types';
import {TableReportSearch} from '../table-report-search';
import {ClearTableColumnFiltersButton} from './actions/clear-table-column-filters-button';
import {ColumnsSettingButton} from './actions/columns-setting-button/columns-setting-button';
import {ComparisonButton} from './actions/comparison-button/comparison-button';
import {ComparisonForm} from './actions/comparison-form/comparison-form';
import {ControlRatiosButton} from './actions/control-ratios-button/control-ratios';
import {DocumentsDownloadButton} from './actions/documents-download-button';
import {RefreshButton} from './actions/refresh-button/refresh-button';
import {ResetDrillDownFilter} from './actions/reset-drilldown-filter';
import {ShowAsideButton} from './actions/show-aside-button/show-aside-button';
import {TableReportPagination} from './actions/table-report-pagination/table-report-pagination';
import {TableReportScaleChanger} from './actions/table-report-scale-changer/table-report-scale-changer';
import {TableReportSideFilter} from './actions/table-report-side-filter';

import './table-report-actions.less';

interface ITableReportActionsProps {
    docId: string | null;
    handleMenuCollapse: () => void;
    handleScaleChange: (value: number) => void;
    pagination: IReportPagination;
    paginationPosition?: EReportConfigurationPosition;
    atLeastOneFilterIsActive?: boolean;
    columns?: TTableReportColumn[];
    hiddenColumnsKeys?: string[];
    hideColumns?: (cols: string[]) => void;
    handleRefreshReportPage?: () => void;
    dataSource: TTableReportBodyItem[] | undefined;
    handlePrint?: () => void;
    hasRolePermission: (func: EUserRoleFunction) => boolean;
    isAllRowsSelected: boolean;
    setIsAllRowsSelected: TStateSetter<boolean>;
    chosenMode?: string;
    setChosenMode: TStateSetter<string>;
    selectedTableRows: number[];
}

export const TableReportActions = ({
    docId,
    handleMenuCollapse,
    handleScaleChange,
    pagination,
    paginationPosition,
    atLeastOneFilterIsActive,
    columns,
    hiddenColumnsKeys,
    hideColumns,
    handleRefreshReportPage,
    dataSource,
    handlePrint,
    hasRolePermission,
    isAllRowsSelected,
    setIsAllRowsSelected,
    selectedTableRows,
    chosenMode,
    setChosenMode,
}: ITableReportActionsProps) => {
    const templateConfig = useAppSelector(selectTableReportTemplateConfig);
    const groupingAttributes = useMemo(() => getGroupingAttributes(templateConfig), [templateConfig]);
    const selectedDocs = useAppSelector(selectDocIdsToRequest);
    const {includeCR, isComparisonFormVisible} = useAppSelector(selectTableReportComparisonOptions);
    const lastRequestParams = useAppSelector(selectLastTableReportLoadPageRequestArgs);

    const {
        actions,
        actionsEnabled,
        templateCode,
        designTemplate,
        // Признаки доступности кнопок (настраиваются в конфигураторе отчетных форм)
        enabledAttachmentsBtn,
        enabledColumnsSettingsBtn,
        enabledComparisonBtn,
        enabledCrBtn,
        enabledDownloadBtn,
        enabledMenu,
        enabledRefreshBtn,
        enabledSearch,
        enabledRowRequests,
        enabledPrint,
        enabledGroupingSettingsBtn,
        enabledSelectAllRowsBtn,
        enabledViewControlRatioBtn,
        enabledDetailedSummaryBtn,
        enabledMenuWhenUsingDrillFilter,
    } = templateConfig ?? {};

    const tableReportConfig = useAppSelector(
        selectTableReportTemplateConfig,
    );
    const showFilterButton = tableReportConfig?.designTemplate === EDesignTemplates.DOCS_FILTERS;

    const showExtraButtons: boolean = !isComparisonFormVisible && !includeCR;

    const history = useHistory();

    return (
        <div className={cn('table-report-actions__wrapper', {
            'table-report-actions__wrapper_sticky': true, // enabledSearch,
        })}
        >
            <div className={cn('table-report-actions')}>
                <div className="table-report-actions__buttons">
                    {(enabledRowRequests && selectedDocs.length > 0) && (
                        <TableOpenModalAction
                            entityName={REQUESTS_ENTITY_NAME}
                            meta={{
                                actionCode: 'CREATE',
                                actionIcon: 'MailOutlined',
                                actionTitle: 'Сформировать запрос',
                                actionType: EMetaActionType.OPEN_MODAL,
                                modalName: 'formDrawer',
                                order: 1,
                            }}
                            url="/requests"
                        />
                    )}

                    {!tableReportConfig && (
                        <Button
                            type="default"
                            icon={<ArrowBackOutlined />}
                            onClick={() => {
                                history.go(-1);
                            }}
                        >
                            Назад
                        </Button>
                    )}

                    {enabledMenu && (
                        <ShowAsideButton handleMenuCollapse={handleMenuCollapse} />
                    )}

                    {enabledMenu && enabledMenuWhenUsingDrillFilter && (
                        <ResetDrillDownFilter />

                    )}

                    {actionsEnabled && actions && actions.length > 0 && (
                        <ActionsKebabMenu
                            actions={actions}
                            selectedTableRows={selectedTableRows}
                            docId={docId}
                            templateCode={templateCode}
                            handleRefreshReportPage={handleRefreshReportPage}
                            isAllRowsSelected={isAllRowsSelected}
                            pageRequestDto={lastRequestParams && {
                                ...omit(lastRequestParams?.dataParams, ['docId', 'lookupType']),
                                size: pagination.pageSize,
                            }}
                        />
                    )}

                    {enabledColumnsSettingsBtn && showExtraButtons && (
                        <ColumnsSettingButton
                            hiddenColumnsKeys={hiddenColumnsKeys}
                            hideColumns={hideColumns}
                        />
                    )}

                    {enabledDownloadBtn && showExtraButtons && (
                        <DocumentsDownloadButton docId={docId} />
                    )}

                    {designTemplate === EDesignTemplates.TABLE_REPORT_DESIGN_TEMPLATE_WITH_PARAMETER_AND_HEADER
                        && enabledDetailedSummaryBtn
                        && (
                            <Radio.Group
                                value={chosenMode}
                                className="custom-radio"
                                onChange={event => setChosenMode(event.target.value)}
                            >
                                <Radio.Button value={EReportViewMode.DETAILED}>Детальный</Radio.Button>
                                <Radio.Button value={EReportViewMode.SUMMARY}>Сводный</Radio.Button>
                            </Radio.Group>
                        )}

                    {enabledAttachmentsBtn
                        && showExtraButtons
                        && hasRolePermission(EUserRoleFunction.VIEW_FILE_AND_COMMENT_IN_REPORT)
                        && (
                            <AttachmentButton
                                templateConfig={templateConfig}
                                docId={docId}
                            />
                        )}

                    {enabledGroupingSettingsBtn
                        && showExtraButtons
                        && hasRolePermission(EUserRoleFunction.GROUPS_SETTING)
                        && (
                            <GroupingSettingsModalOpener
                                attributesEntries={groupingAttributes}
                                templateConfig={templateConfig}
                            />
                        )}

                    {enabledComparisonBtn
                    && !includeCR
                    && hasRolePermission(EUserRoleFunction.ADD_TO_CAMPARISON)
                    && (<ComparisonButton />)}

                    {enabledCrBtn
                    && !isComparisonFormVisible
                    && hasRolePermission(EUserRoleFunction.VIEW_CR)
                    && (<ControlRatiosButton />)}

                    {enabledRefreshBtn && showExtraButtons && (
                        <RefreshButton
                            handleRefreshReportPage={handleRefreshReportPage}
                        />
                    )}

                    <ClearTableColumnFiltersButton
                        setTableColumnFilters={tableReportSliceActions.setTableColumnFilters}
                        selectTableFiltersDataAdaptedForRequest={selectTableReportColumnFiltersDataAdaptedForRequest}
                    />

                    {showFilterButton && <TableReportSideFilter />}

                    {enabledSearch && (
                        <TableReportSearch
                            dataSource={dataSource}
                            columns={columns}
                        />
                    )}

                    {enabledPrint && (
                        <Button
                            type="primary"
                            icon={<PrinterOutlined />}
                            onClick={handlePrint}
                        >
                            Печать
                        </Button>
                    )}

                    {enabledSelectAllRowsBtn && (
                        <Button
                            type="default"
                            disabled={!dataSource?.length}
                            onClick={() => setIsAllRowsSelected(p => !p)}
                        >
                            {isAllRowsSelected ? 'Отменить выделение' : 'Выделить все'}
                        </Button>
                    )}

                    {docId
                    && enabledViewControlRatioBtn
                    && hasRolePermission(EUserRoleFunction.VIEW_CONTROL_RATIO_IN_REPORT)
                    && (
                        <ControlRatiosActionsButtons docId={docId} />
                    )}
                </div>
                <div className="table-report-actions__controls">
                    <TableReportPagination
                        pagination={pagination}
                        paginationPosition={paginationPosition}
                        atLeastOneFilterIsActive={atLeastOneFilterIsActive}
                    />
                    <TableReportScaleChanger handleScaleChange={handleScaleChange} />
                </div>
            </div>
            {isComparisonFormVisible && (
                <ComparisonForm
                    docId={docId}
                />
            )}
        </div>
    );
};
